import React from "react";
import { createRoot } from "react-dom/client";
import { Provider } from "react-redux";
import { ConfigProvider } from "antd";
import { BrowserRouter } from "react-router-dom";

import "../src/assets/css/style.css";
import "./index.css";

import reportWebVitals from "./reportWebVitals";
import Routes from "./routes";
import store from "./store";
import { authCheck } from "./store/actions/user";
import { BrandProvider, useBrand } from "./context/BrandContext";
import { loadSavedTheme, DEFAULT_THEME_KEY, getThemeByKey } from "./utils/themeManager";

// Wires AntD's own theme (colorPrimary, used by every native AntD component —
// Steps, Switch, Radio, type="primary" Button, focus rings, etc.) to the same
// dynamic brand color the app's custom components already read from the
// --colorDark CSS variable — without this, AntD components silently fall
// back to AntD's default blue regardless of the active brand theme.
function ThemedApp() {
  const { activeTheme } = useBrand();
  const primary = activeTheme?.dark || getThemeByKey(DEFAULT_THEME_KEY).dark;

  return (
    <ConfigProvider
      theme={{
        token: {
          controlHeight: 36,
          borderRadius: 6,
          fontSize: 14,
          // AntD v5 generates its own CSS-in-JS with its own default
          // font stack, independent of body's font-family — without
          // this, every plain antd component (Button, Select, Input,
          // Table...) silently falls back to AntD's default typeface
          // instead of the app's Plus Jakarta Sans, while our custom
          // native-HTML components (CustomButton, etc.) stay correct.
          // Setting it once here is the single source of truth for
          // every antd-based control app-wide.
          fontFamily: "'Plus Jakarta Sans', 'Noto Sans', sans-serif",
          colorPrimary: primary,
        },
      }}
    >
      <Routes />
    </ConfigProvider>
  );
}

// Stale build reference (a browser tab open across a deploy, or a cached
// index.html pointing at chunk hashes a later deploy removed) surfaces as a
// ChunkLoadError from a lazy route import — reload once to fetch the current
// build instead of leaving the user stuck on a dead route.
const RELOAD_FLAG = "chunkLoadErrorReload";
function handleChunkLoadError(error) {
  if (error?.name !== "ChunkLoadError") return;
  if (sessionStorage.getItem(RELOAD_FLAG)) return;
  sessionStorage.setItem(RELOAD_FLAG, "1");
  window.location.reload();
}
window.addEventListener("error", (event) => handleChunkLoadError(event.error));
window.addEventListener("unhandledrejection", (event) =>
  handleChunkLoadError(event.reason),
);
window.addEventListener("load", () => sessionStorage.removeItem(RELOAD_FLAG));

// Run auth check before rendering
store.dispatch(authCheck());

const container = document.getElementById("root");
const root = createRoot(container);
// loadSavedTheme(); // 🔥 BEFORE React render

root.render(
  <React.StrictMode>
    <BrowserRouter>
      <Provider store={store}>
        <BrandProvider>
          <ThemedApp />
        </BrandProvider>
      </Provider>
    </BrowserRouter>
  </React.StrictMode>,
);

reportWebVitals();
